/**
 * Capture the utm params this app was opened with, so they survive both an
 * internal route change and the Clerk OAuth round trip.
 *
 * WHY THIS EXISTS. Measured 2026-08-18 against HTUserFunnel: only ~30% of
 * signups carry any UtmSource, and only ~18% carry the consent-gated `tt_attr`
 * cookie. Attribution resolves from the URL first and that cookie second, so a
 * visitor who declines analytics consent is attributable ONLY while the query
 * string is still on the URL.
 *
 * It usually is not, and the marketing site's own header comment says why: the
 * "Log in" link lands on `/?utm_source=homepage`, the visitor then follows this
 * app's Sign up link, and the query string is dropped on the way. From that
 * point attribution depends entirely on a cookie five in six people do not
 * have. `useSignupTracking` already writes these keys, but it only runs on the
 * Signup page, so it never sees the arrival that landed anywhere else.
 *
 * This runs at entry, on whatever route the app was opened with.
 *
 * DELIBERATELY NOT CONSENT-GATED, and that is the whole point. A utm_source is
 * a campaign label we put on our own link, not information about the person
 * following it, and it is already in the URL the browser just requested.
 * Session-scoped, first-party, cleared when the tab closes. If this is ever
 * made conditional it stops solving the problem it was written for.
 *
 * Keys match `useSignupTracking`, which reads them as its fallback. Do not
 * rename one without the other.
 */

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign'] as const;

type UtmKey = (typeof UTM_KEYS)[number];

export interface SessionUtm {
  source: string | null;
  medium: string | null;
  campaign: string | null;
}

const read = (key: UtmKey): string | null => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    // Safari private mode throws on sessionStorage access. Attribution is worth
    // less than the app booting.
    return null;
  }
};

/**
 * Persist any utm params on the current URL. Last touch wins, matching the
 * marketing site's `s` field and `useSignupTracking`: a later tagged arrival
 * describes the click that actually brought this visit.
 *
 * Call before the first route change. React Router rewrites the URL as soon as
 * it mounts, and on some entry paths that drops the query string.
 */
export const captureUtmFromUrl = (): void => {
  try {
    const params = new URLSearchParams(window.location.search);
    for (const key of UTM_KEYS) {
      const value = params.get(key);
      if (value) sessionStorage.setItem(key, value);
    }
  } catch {
    // As above: never let attribution break boot.
  }
};

/** What was captured this session, if anything. */
export const readUtmSession = (): SessionUtm | null => {
  const source = read('utm_source');
  if (!source) return null;
  return {
    source,
    medium: read('utm_medium'),
    campaign: read('utm_campaign'),
  };
};
